import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Progress, Button, Flex, Typography } from 'antd';
import {
  DashboardOutlined,
  PhoneOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { colors } from '../../../../domain/types/colors';
import type { LeedbucksUsage } from '../../../hooks/plans/useCompanyCredits';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../../pages/subscriptions/SubscriptionsPage';
import type { CreditStatusFilter } from '../../../pages/subscriptions/controllers/useSubscriptionsPageController';
import '../stats-card/StatCard.css';

import styles from './PlanUsageCard.module.css';

const { Text } = Typography;

interface PlanUsageCardProps {
  usage: LeedbucksUsage;
  onUnderstand: () => void;
  loading?: boolean;
  ratesUnavailable?: boolean;
  nextBillingDate?: string; // Stripe period end, already formatted. Absent without a billing mirror
}

const formatNumber = (num: number): string => num.toLocaleString('fr-FR');
const formatEuro = (value: number): string => value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PlanUsageCard: React.FC<PlanUsageCardProps> = ({ usage, onUnderstand, loading, ratesUnavailable, nextBillingDate }) => {
  const navigate = useNavigate();
  const goToNumbers = (statusFilter: CreditStatusFilter) =>
    navigate(SUBSCRIPTIONS_PAGE_PATH, { state: { statusFilter } });

  const isOverage = usage.usagePercentage > 100;
  const gaugeWidth = Math.min(100, Math.max(0, usage.usagePercentage));
  const remainingPercent = Math.max(0, 100 - usage.usagePercentage);
  const depleted = !isOverage && remainingPercent === 0;
  const isLow = remainingPercent < 20;
  const gaugeColor = isLow ? '#f59e0b' : '#22c55e';
  const statusColor = isLow ? '#f59e0b' : '#22c55e';

  const statusLabel = isOverage
    ? `${usage.usagePercentage - 100} % de dépassement`
    : depleted
      ? 'épuisé'
      : `${remainingPercent} % restant${remainingPercent > 1 ? 's' : ''}`;

  // Counted number by number at each one's own rates, unlimited when every one of them gets the action for free
  const minutesEquivalent = usage.minutesEquivalent !== null ? formatNumber(usage.minutesEquivalent) : '-';
  const smsEquivalent = usage.smsEquivalent !== null ? formatNumber(usage.smsEquivalent) : '-';
  const freeCalls = usage.minutesEquivalent === Infinity;
  const freeSms = usage.smsEquivalent === Infinity;

  return (
    <Card className="stat-card" loading={loading}>
      <div className="stat-card__header">
        <div className="stat-card__icon stat-card__icon--green">
          <DashboardOutlined style={{ fontSize: 24, color: colors.primary.main }} />
        </div>
        <span className="stat-card__label">Forfait du cycle en cours</span>
        <Button
          type="link"
          size="small"
          icon={<QuestionCircleOutlined />}
          onClick={onUnderstand}
          style={{ marginLeft: 'auto', color: colors.primary.main }}
        >
          Comprendre
        </Button>
      </div>

      <div className="stat-card__body">
        {usage.isUnlimited ? (
          <>
            <span className="stat-card__value">∞</span>
            <Text type="secondary">crédits illimités</Text>
          </>
        ) : (
          <>
            <Flex align="baseline" gap={10} wrap="wrap">
              <span className="stat-card__value">{formatNumber(usage.used)}</span>
              <Text type="secondary">crédits consommés sur {formatNumber(usage.total)}</Text>
              <Text strong style={{ marginLeft: 'auto', color: statusColor }}>
                {statusLabel}
              </Text>
            </Flex>
            <Progress
              percent={gaugeWidth}
              showInfo={false}
              strokeColor={gaugeColor}
              trailColor="#e5e7eb"
              className="stat-card__progress"
            />
            <Flex vertical gap={6} className={styles.equivalences}>
              <Text type="secondary">
                <PhoneOutlined className={styles.icon} />
                {freeCalls ? <>Soit des <Text strong>appels illimités</Text></> : <>Soit jusqu'à <Text strong>{minutesEquivalent} min d'appels</Text></>}
              </Text>
              <Text type="secondary">
                <MessageOutlined className={styles.icon} />
                {freeSms ? <>ou des <Text strong>SMS illimités</Text></> : <>ou jusqu'à <Text strong>{smsEquivalent} SMS</Text></>}
              </Text>
              {usage.nextResetLabel && (
                <Text type="secondary">
                  <DashboardOutlined className={styles.icon} />
                  Recharge du forfait · <Text strong>{usage.nextResetLabel}</Text>
                </Text>
              )}
              {nextBillingDate && (
                <Text type="secondary">
                  <InfoCircleOutlined className={styles.icon} />
                  Prochaine facturation le <Text strong>{nextBillingDate}</Text>
                </Text>
              )}
            </Flex>
            <Text type="secondary" className={styles.note}>
              {ratesUnavailable ? (
                <>
                  <WarningOutlined className={styles.icon} />
                  Équivalences appels / SMS indisponibles pour le moment.
                </>
              ) : (
                <>
                  <InfoCircleOutlined className={styles.icon} />
                  Les appels et SMS d'un même numéro utilisent le même forfait.
                </>
              )}
            </Text>
            {(usage.overageCount > 0 || usage.exhaustedCount > 0 || usage.lowCreditCount > 0) && (
              <Flex gap={8} wrap="wrap" className={styles.alerts}>
                {usage.overageCount > 0 && (
                  <button type="button" className={styles.alertPill} onClick={() => goToNumbers('overage')}>
                    <WarningOutlined className={styles.alertIcon} />
                    {usage.overageCount} en hors forfait · {formatEuro(usage.overageAmount)} €
                  </button>
                )}
                {usage.exhaustedCount > 0 && (
                  <button type="button" className={styles.alertPill} onClick={() => goToNumbers('exhausted')}>
                    <WarningOutlined className={styles.alertIcon} />
                    {usage.exhaustedCount} forfait{usage.exhaustedCount > 1 ? 's' : ''} épuisé{usage.exhaustedCount > 1 ? 's' : ''}
                  </button>
                )}
                {usage.lowCreditCount > 0 && (
                  <button type="button" className={styles.alertPill} onClick={() => goToNumbers('warning')}>
                    <WarningOutlined className={styles.alertIcon} />
                    {usage.lowCreditCount} forfait{usage.lowCreditCount > 1 ? 's' : ''} à surveiller
                  </button>
                )}
              </Flex>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

export default PlanUsageCard;
